/**
 * The small parts a roadmap item's dialog is built from: the chip strip under the title (a
 * labelled value, or a bare select that reads as one), the 44 px icon buttons in its header, the
 * footer's big buttons, and the link tiles (Brief, Jira, Figma…).
 */
import type { ReactNode } from 'react';
import { ArrowUpRight, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

/** A labelled chip in the strip: small uppercase label, then the value or its select. */
export function Chip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex h-9 items-center gap-2 whitespace-nowrap rounded-[9px] border border-[hsl(var(--ns-acai)/0.12)] bg-[hsl(var(--ns-acai)/0.04)] px-3">
      <span className="text-xs tracking-[0.06em] text-[hsl(var(--ns-boulder))]">
        {label.toUpperCase()}
      </span>
      {children}
    </label>
  );
}

/** A bare native select that reads as the chip's value. */
export function ChipSelect({
  value,
  label,
  className,
  onChange,
  children,
}: {
  value: string;
  label: string;
  className?: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <select
      value={value}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      className={cn('cursor-pointer appearance-none bg-transparent outline-none', className)}
    >
      {children}
    </select>
  );
}

/** `active` makes it a toggle (pressed or not); without it, a plain button. */
export function IconButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
      className={cn(
        'grid size-11 place-items-center rounded-[10px] border border-[hsl(var(--ns-acai)/0.14)] text-primary',
        active ? 'bg-[hsl(var(--ns-pebble)/0.4)]' : 'bg-background',
      )}
    >
      {children}
    </button>
  );
}

export function FooterButton({
  primary = false,
  onClick,
  children,
}: {
  primary?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-12 whitespace-nowrap rounded-[10px] border px-[22px] text-base',
        primary
          ? 'border-primary bg-primary text-[hsl(var(--ns-unbleached))]'
          : 'border-[hsl(var(--ns-acai)/0.14)] bg-background text-primary',
      )}
    >
      {children}
    </button>
  );
}

/**
 * A link tile. Filled: opens the link. Empty with `onEmpty`: a button, with a faint plus — an
 * editor's way into adding the link. Empty without it: a greyed placeholder that says so
 * (`emptyText`) and does nothing.
 */
export function LinkTile({
  icon,
  label,
  url,
  sub,
  tag,
  emptyText = 'Add link',
  onEmpty,
}: {
  icon: ReactNode;
  label: string;
  url: string;
  /** The second line when there is a link; the link itself, shortened, by default. */
  sub?: ReactNode;
  /** Something beside the label (a feature flag's rollout state). */
  tag?: ReactNode;
  emptyText?: string;
  onEmpty?: () => void;
}) {
  const present = url !== '';
  const placeholder = !present && !onEmpty;
  const body = (
    <>
      <span className="grid size-8 shrink-0 place-items-center rounded-[7px] bg-[hsl(var(--ns-acai)/0.04)] text-primary">
        {icon}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              'truncate text-sm font-semibold leading-[1.15]',
              placeholder && 'text-[hsl(var(--ns-boulder))]',
            )}
          >
            {label}
          </span>
          {tag}
        </span>
        <span className="truncate text-xs leading-[1.2] text-[hsl(var(--ns-boulder))]">
          {present ? (sub ?? url.replace(/^https?:\/\//, '')) : emptyText}
        </span>
      </span>
      {!placeholder && (
        <span className={cn('shrink-0 text-primary', !present && 'opacity-45')}>
          {present ? (
            <ArrowUpRight className="size-[13px]" strokeWidth={1.75} />
          ) : (
            <Plus className="size-[13px]" strokeWidth={1.75} />
          )}
        </span>
      )}
    </>
  );
  const tile =
    'flex min-w-0 items-center gap-2.5 rounded-[10px] border border-[hsl(var(--ns-acai)/0.14)] bg-background py-2 pl-2 pr-3 text-left';
  if (present)
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={cn(tile, 'hover:border-[hsl(var(--ns-acai)/0.32)]')}
      >
        {body}
      </a>
    );
  if (!onEmpty) return <div className={cn(tile, 'bg-[hsl(var(--ns-acai)/0.02)]')}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onEmpty}
      className={cn(tile, 'hover:border-[hsl(var(--ns-acai)/0.32)]')}
    >
      {body}
    </button>
  );
}
